import {
  minsToHHMM,
  getDayType,
  getDayTypeLabel,
  getScheduleKey,
  firstTime,
  escapeHtml,
  resolveDirection,
  getAppDate
} from "./utils.js";
import { calcNextTrain } from "./trains";
import { getStopName } from "./line-config.js";
import { getAlertsForLine } from "./alerts.js";
import { STOP_COORDINATES } from "./map-data.js";
import { patchDOM } from "./dom-utils.js";
import { getFeedStatus } from "./gtfs-status.js";
import {
  getVisibleStops,
  getReferenceStops,
  getReferenceTime,
  getAllStopsOrdered,
  realignStopsToRoute,
  formatFeedDate
} from "./timetable-data.js";
import { bindTimetableEvents } from "./timetable-events.js";
import { renderTimetableSearchHTML } from "./timetable-search.js";
import { renderPinnedTripBanner, isPinnedTripInCurrentContext } from "./timetable-pin.js";
import { getThresholdsFromState, classifyConnection } from "./connection-classifier";
import { shortStop } from "./stop-display";
import { clusterTrips } from "./timetable-clusters";
import { filterByWindow } from "./timetable-filter";
import { getNextDepartures } from "./timetable-data";

let lastArgs = null;

function renderTimetableHeader({
  activeLine,
  config,
  cfg,
  state,
  lineData,
  lineConfig,
  activeLineOrder,
  currentMin,
  crossDayBrowsing,
  dayType,
  direction,
  baseDirection
}) {
  return `
    <div class="page-head" style="--lc: var(--line-${activeLine}, var(--accent));">
      <div style="display:flex; justify-content:space-between; align-items:flex-end; gap:12px;">
        <div>
          <h1>Orari</h1>
          <p>${escapeHtml(config.label)} · ${escapeHtml(cfg.activeCityConfig?.name || "Z-Transit")}</p>
        </div>
        <div class="view-toggle" id="toggle-classic-view" role="tablist" aria-label="Vista Orari" style="--lc: var(--line-${activeLine}, var(--accent));">
          <button type="button" data-view="card" class="${state.timetableClassicView ? "" : "active"}" role="tab" aria-selected="${state.timetableClassicView ? "false" : "true"}">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="12" height="4" rx="1.2"/><rect x="2" y="9" width="12" height="4" rx="1.2"/></svg>
            Card
          </button>
          <button type="button" data-view="table" class="${state.timetableClassicView ? "active" : ""}" role="tab" aria-selected="${state.timetableClassicView ? "true" : "false"}">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 4h12M2 8h12M2 12h12"/></svg>
            Tabella
          </button>
        </div>
      </div>
      ${renderTimetableSearchHTML()}
    </div>

    ${renderPinnedTripBanner(state, lineData, lineConfig, currentMin, crossDayBrowsing)}
    <!-- Line Selector -->
    <div class="line-select">
      ${activeLineOrder
        .filter((lineId) => lineConfig[lineId])
        .map((lineId) => {
          const isOutsideCity = !(cfg.lineOrder || []).includes(lineId);
          const isActive = lineId === activeLine;
          const hasAlerts = getAlertsForLine(lineId).length > 0;
          const alertIndicator = hasAlerts
            ? `<span title="Disservizi" style="color: var(--danger); font-size: 0.8em; position: absolute; top: 6px; right: 6px;">⚠️</span>`
            : "";
          return `
            <button type="button" data-line="${lineId}" class="line-pill ${isActive ? "active" : ""} ${isOutsideCity ? "line-outside" : ""}" style="--lc: var(--line-${lineId}, var(--accent));">
              ${alertIndicator}
              <span class="lbl">${lineId}</span>
              <span class="bar"></span>
            </button>
          `;
        })
        .join("")}
      <button type="button" data-toggle-timetable-all-lines class="line-pill line-pill-add" title="${state.timetableShowAllLines ? "Riduci alle linee della città" : "Mostra tutte le linee"}" aria-label="${state.timetableShowAllLines ? "Riduci linee" : "Mostra tutte le linee"}">
        <span class="lbl">${state.timetableShowAllLines ? "−" : "+"}</span>
        <span class="bar"></span>
      </button>
    </div>

    <!-- Day filter + Direction -->
    <div class="filter-row" style="--lc: var(--line-${activeLine}, var(--accent));">
      <div class="day-filter">
        ${["weekday", "saturday", "sunday"]
          .map((dt) => {
            const key = getScheduleKey(activeLine, dt, direction);
            // V5: disable Sabato/Festivo when the line collapses its schedule
            // into the weekday key (Z647: always weekday_*; Z644: no sunday_*).
            // Otherwise all three buttons render enabled showing the same data.
            const weekdayKey = getScheduleKey(activeLine, "weekday", direction);
            const collapsesToWeekday = dt !== "weekday" && key === weekdayKey;
            const disabled = !lineData[activeLine]?.[key]?.length || collapsesToWeekday;
            const isActive = dayType === dt;
            return `<button type="button" data-day="${dt}" class="${isActive ? "active" : ""}" ${disabled ? "disabled" : ""}>${getDayTypeLabel(dt)}</button>`;
          })
          .join("")}
      </div>
      <button type="button" class="direction-toggle" data-dir="${baseDirection === "outbound" ? "return" : "outbound"}">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          ${baseDirection === "outbound"
            ? `<path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`
            : `<path d="M12 7H2M6 3L2 7l4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`}
        </svg>
        <span>${baseDirection === "outbound" ? "Andata" : "Ritorno"}</span>
      </button>
    </div>
  `;
}

export function renderTimetable(state, lineData, lineConfig, cfg, saveSettings) {
  lastArgs = { state, lineData, lineConfig, cfg, saveSettings };
  const container = document.getElementById("timetable-content");
  if (!container) return;

  const now = getAppDate();
  const currentMin = now.getHours() * 60 + now.getMinutes();
  const dayType = state.timetableDayType || getDayType(now, cfg);
  const baseDirection = state.timetableDirection || "outbound";
  const direction = resolveDirection(baseDirection, state.settings);

  // P3: detect cross-day browsing — user is browsing a day-type that doesn't
  // match today's actual day-type. When true, suppress countdowns and past/
  // future split (countdown math makes no sense against tomorrow's schedule
  // — every trip would be "partito" or every trip "in 600 min"). Render a
  // neutral flat list with a hint that these are not real-time hours.
  // Computed early so the pinned-trip banner can also suppress its countdown
  // when the pin sits in a non-current day.
  const actualDayType = getDayType(now, cfg);
  const crossDayBrowsing = dayType !== actualDayType;

  // Calculate customized/followed lines active in the focus city, matching renderLive in live.js.
  // This ensures the custom visible lines are synchronized between the LIVE and TIMETABLE tabs.
  const focusCity = state.settings?.focusCity || cfg.defaults?.focusCity || "BT";
  const followedForCity = state.settings?.followedLinesByCity?.[focusCity];
  const userActiveLines = state.settings?.userProfile?.activeLines;
  const cityLines = cfg.lineOrder || Object.keys(lineConfig);

  let activeLineOrder;
  const hasCustomLines =
    Array.isArray(followedForCity) ||
    (Array.isArray(userActiveLines) &&
      userActiveLines.length > 0 &&
      !state.settings?.userProfile?.skipped &&
      focusCity === (state.settings?.userProfile?.focusCity || "BT"));

  let defaultActiveLineOrder;
  if (Array.isArray(followedForCity)) {
    defaultActiveLineOrder = followedForCity.filter((id) => lineConfig[id]);
  } else if (
    Array.isArray(userActiveLines) &&
    userActiveLines.length > 0 &&
    !state.settings?.userProfile?.skipped &&
    focusCity === (state.settings?.userProfile?.focusCity || "BT")
  ) {
    defaultActiveLineOrder = cityLines.filter((id) => userActiveLines.includes(id));
  } else {
    defaultActiveLineOrder = cityLines;
  }

  if (state.timetableShowAllLines) {
    activeLineOrder = Object.keys(lineConfig).sort();
  } else {
    activeLineOrder = defaultActiveLineOrder;
  }

  const cityOrCustomLinesCount = defaultActiveLineOrder.length;

  const activeLine = state.timetableLine || activeLineOrder?.[0] || cfg.lineOrder?.[0] || "Z649";
  const config = lineConfig[activeLine];

  if (!config) {
    patchDOM(container, `<div class="empty-state">Seleziona una linea</div>`);
    return;
  }

  // If line data hasn't been loaded yet, show a loading message
  if (!lineData[activeLine]) {
    patchDOM(
      container,
      `<div class="empty-state">Dati della linea ${escapeHtml(activeLine)} non ancora caricati. Prova a cambiare città nelle Impostazioni.</div>`
    );
    return;
  }

  const scheduleKey = getScheduleKey(activeLine, dayType, direction);
  const trips = [...(lineData[activeLine]?.[scheduleKey] || [])];

  // Initialize timetableTempStops if in edit mode and not yet set
  if (state.timetableEditMode && !state.timetableTempStops) {
    const currentStops =
      state.settings?.timetableStops?.[activeLine]?.[scheduleKey] ||
      state.settings?.timetableStops?.[activeLine]?.[direction] ||
      getVisibleStops(state, cfg, activeLine, scheduleKey, direction, config.referenceStops || []);
    state.timetableTempStops = [...currentStops];
  }

  // Bug-fix: realign timetableTempStops to route order on every render
  // while in edit mode. This way a previously-saved out-of-order pair gets
  // visually corrected when the user re-opens edit mode, and the upcoming
  // toggle handler can safely push() then realign without landing new
  // columns at the end of the table.
  if (state.timetableEditMode && state.timetableTempStops?.length) {
    const dayTypes = ["weekday", "saturday", "sunday"];
    const dirTrips = [];
    for (const dt of dayTypes) {
      const k = getScheduleKey(activeLine, dt, direction);
      for (const t of lineData[activeLine]?.[k] || []) dirTrips.push(t);
    }
    const refStops = getAllStopsOrdered(dirTrips);
    state.timetableTempStops = realignStopsToRoute(state.timetableTempStops, refStops);
  }

  let stops;
  if (state.timetableEditMode) {
    // Build the complete route stop list for the current direction, merging
    // all 3 day-types. Previously a naive "first-seen" push was used which
    // put stops only served by some trips at the end of the list instead of
    // their actual route position. Use getAllStopsOrdered which preserves
    // logical order, then dedupe defensively.
    const dayTypes = ["weekday", "saturday", "sunday"];
    const dirTrips = [];
    for (const dt of dayTypes) {
      const key = getScheduleKey(activeLine, dt, direction);
      const dtTrips = lineData[activeLine]?.[key] || [];
      for (const trip of dtTrips) dirTrips.push(trip);
    }
    stops = getAllStopsOrdered(dirTrips);
  } else if (state.showAllStops) {
    stops = getAllStopsOrdered(trips);
  } else {
    stops = getVisibleStops(
      state,
      cfg,
      activeLine,
      scheduleKey,
      direction,
      config.referenceStops || []
    );
  }

  // S2: Validate timetableToStop against the CURRENT direction's trips only.
  // Previously validated against the all-directions union (Object.values(...).flat()),
  // which let wrong-direction codes (e.g. Z649 return codes while browsing outbound)
  // silently pass validation, then empty-column filter removed them and the
  // card/table rendered garbage.
  if (state.timetableToStop) {
    const dirStops = getAllStopsOrdered(trips);
    if (dirStops.length > 0 && !dirStops.includes(state.timetableToStop)) {
      delete state.timetableToStop;
    }
  }

  // Hoisted earlier (used by S4: empty-stops filter must know whether the
  // classic table will render before deciding to mutate `stops`).
  const useModernCards = !state.timetableEditMode && !state.timetableClassicView;

  // S4: Empty-stops filter is designed for the classic table layout; on the
  // modern card view it can destroy `stops[0]` (the Da) by removing a
  // reference stop whose codes happen to have no trips today. Apply only
  // when rendering the classic table, and never let it filter down to an
  // empty list (keep at least the first stop).
  let emptyStopsBanner = "";
  if (!state.timetableEditMode && !state.showAllStops && trips.length > 0 && stops.length > 0 && !useModernCards) {
    const emptyStops = stops.filter((code) => {
      return trips.every((trip) => trip.stops?.[code] === undefined || trip.stops?.[code] === null);
    });

    if (emptyStops.length > 0) {
      if (!state.showEmptyStops) {
        const filtered = stops.filter((code) => !emptyStops.includes(code));
        // Never filter to an empty list — keep at least the first stop so
        // the From/A selector + cards always have a valid Da.
        stops = filtered.length > 0 ? filtered : [stops[0]];
        emptyStopsBanner = `
          <div class="empty-stops-indicator empty-stops-indicator--hidden">
            <span class="empty-stops-text--hidden"><span class="empty-stops-icon">🛈</span><strong>${emptyStops.length}</strong> ${emptyStops.length === 1 ? "colonna vuota nascosta" : "colonne vuote nascoste"} (senza corse oggi)</span>
            <button type="button" class="text-btn toggle-empty-stops-btn" id="toggle-empty-stops">Mostra tutto</button>
          </div>
        `;
      } else {
        emptyStopsBanner = `
          <div class="empty-stops-indicator empty-stops-indicator--shown">
            <span class="empty-stops-text--shown"><span class="empty-stops-icon">🛈</span>Mostrate <strong>${emptyStops.length}</strong> ${emptyStops.length === 1 ? "colonna vuota" : "colonne vuote"}</span>
            <button type="button" class="text-btn toggle-empty-stops-btn" id="toggle-empty-stops">Nascondi vuote</button>
          </div>
        `;
      }
    }
  }

  const referenceStops = getReferenceStops(state, cfg, activeLine, direction, stops);

  // Apply quick filter
  const windowMin = state.timetableFilterWindow || "all";
  const filteredTrips = filterByWindow(trips, currentMin, windowMin, referenceStops);

  const sortedTrips = filteredTrips
    .map((trip) => ({ ...trip, _refMin: getReferenceTime(trip, referenceStops) }))
    .sort(
      (a, b) => (a._refMin ?? Number.POSITIVE_INFINITY) - (b._refMin ?? Number.POSITIVE_INFINITY)
    );

  // Edit Button HTML.
  // §3: this was computed but never concatenated into the page DOM, so the
  // ✏️ Modifica Fermate button (and its bindings) was unreachable. Now we
  // surface it as a small action row under the Da/A selector. Only relevant
  // in classic-table view (in Card view editing single FROM/A via the
  // sheets already gives full control over the visible stops).
  const editButtonHtml =
    state.showAllStops || useModernCards
      ? ""
      : state.timetableEditMode
        ? `
            <button type="button" class="btn-premium btn-save-timetable" id="save-timetable-stops">Salva ✓</button>
            <button type="button" class="btn-premium btn-cancel-timetable" id="cancel-timetable-stops">Annulla ×</button>
          `
        : `
            <button type="button" class="timetable-tools-trigger" id="edit-timetable-stops" title="Personalizza colonne fermate per questa direzione">⚙️ <span>Personalizza colonne</span></button>
          `;

  // Calculate average duration if destination stop is set
  let avgDurationHtml = "";
  if (state.timetableToStop && stops.length > 0) {
    const durations = [];
    trips.forEach((trip) => {
      const f = trip.stops?.[stops[0]];
      const t = trip.stops?.[state.timetableToStop];
      if (f !== undefined && f !== null && t !== undefined && t !== null && t > f) {
        durations.push(t - f);
      }
    });
    if (durations.length > 0) {
      const avg = Math.round(durations.reduce((a, b) => a + b, 0) / durations.length);
      avgDurationHtml = `durata media ~${avg} min`;
    }
  }

  let html = "";
  // Hoisted out of the useModernCards block (line ~217) so the modern-card
  // trip-card render call-sites further down (around line 479/491, inside a
  // separate useModernCards scope) can also read this line color.
  const activeColor = cfg.color || "#06b6d4";

  // "Segui questa corsa": if the active pin references a line that's not yet
  // loaded into lineData, trigger a lazy async load + re-render so the banner
  // can show trip info (dep/arr/countdown) instead of the "non trovata"
  // fallback. Fire-and-forget — the banner's clear button still works in
  // the meantime.
  const _pin = state.timetablePinnedTrip;
  if (_pin?.lineId && lineConfig[_pin.lineId] && !lineData[_pin.lineId]) {
    import("./line-registry.js")
      .then(({ loadSingleLine }) => loadSingleLine(_pin.lineId))
      .then((data) => {
        if (data) {
          lineData[_pin.lineId] = data;
          renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
        }
      })
      .catch((e) => console.warn(`[Trasporti] Errore caricamento (pin) ${_pin.lineId}:`, e));
  }

  html += renderTimetableHeader({
    activeLine,
    config,
    cfg,
    state,
    lineData,
    lineConfig,
    activeLineOrder,
    currentMin,
    crossDayBrowsing,
    dayType,
    direction,
    baseDirection
  });

  html += `
    <!-- Da -> A Stop Selection & Swap Bar -->
    <div class="stop-selector" style="--lc: var(--line-${activeLine}, var(--accent));">
      <div class="ss-card">

        <!-- From Stop — div (not button) so the nested .pin-btn button is valid HTML -->
        <div class="ss-row from-stop-trigger" id="timetable-from-trigger" data-line="${activeLine}" data-dir="from" role="button" tabindex="0" aria-label="Scegli fermata di partenza">
          <span class="ss-mark"><span class="circle"></span><span class="seg"></span></span>
          <div class="ss-text">
            <div class="lbl-mini">Da</div>
            <div class="val">
              <span>${escapeHtml(stops[0] ? (getStopName(stops[0]) || stops[0]) : 'Seleziona...')}</span>
              ${stops[0] ? `<button type="button" class="pin-btn" data-stop-code="${escapeHtml(stops[0])}" data-stop-name="${escapeHtml(getStopName(stops[0]) || stops[0])}" aria-label="Apri ${escapeHtml(getStopName(stops[0]) || stops[0])} sulla mappa" title="Apri sulla mappa"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></button>` : ""}
            </div>
          </div>
        </div>

        <!-- Divider + Swap -->
        <div class="ss-swap-wrap">
          <button type="button" class="ss-swap" id="timetable-swap-trigger" title="Inverti direzione" aria-label="Inverti direzione">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M4 2L4 12M4 12L2 10M4 12L6 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 12L10 2M10 2L8 4M10 2L12 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- To Stop -->
        <div class="ss-row ${state.timetableToStop ? "to-stop-trigger" : "add-arrival-cta"}" id="timetable-to-trigger" data-line="${activeLine}" data-dir="to" role="button" tabindex="0" aria-label="Scegli fermata di arrivo">
          <span class="ss-mark"><span class="seg rev"></span><span class="square"></span></span>
          <div class="ss-text">
            <div class="lbl-mini">A</div>
            <div class="val">
              <span>${state.timetableToStop ? escapeHtml(getStopName(state.timetableToStop) || state.timetableToStop) : 'Aggiungi arrivo...'}</span>
              ${state.timetableToStop ? `
                <button type="button" class="pin-btn" data-stop-code="${escapeHtml(state.timetableToStop)}" data-stop-name="${escapeHtml(getStopName(state.timetableToStop) || state.timetableToStop)}" aria-label="Apri ${escapeHtml(getStopName(state.timetableToStop) || state.timetableToStop)} sulla mappa" title="Apri sulla mappa"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></button>
                <button type="button" class="pin-btn" id="timetable-clear-to-stop" aria-label="Rimuovi arrivo" title="Rimuovi arrivo" style="color: var(--danger);"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></button>
              ` : ""}
            </div>
          </div>
        </div>
      </div>
      ${editButtonHtml ? `<div class="ss-actions" style="display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; margin-top:8px; padding: 0 8px;">${editButtonHtml}</div>` : ''}
      ${avgDurationHtml ? `<div style="font-size: 0.75rem; color: var(--text-muted, var(--muted)); margin-top: 8px; text-align: right; padding-right: 8px;">${escapeHtml(avgDurationHtml)}</div>` : ''}
    </div>`;

  if (!trips.length && !state.timetableEditMode) {
    html += `<div class="empty-state">Nessun orario per ${getDayTypeLabel(dayType).toLowerCase()} in questa direzione.</div>`;
    patchDOM(container, html, {
      onAfterPatch: () =>
        bindTimetableEvents(container, { getArgs: () => lastArgs, renderTimetable }, saveSettings)
    });
    return;
  }

  html += emptyStopsBanner;

  // Filter header above table/clusters
  let filterHeaderHtml = "";
  if (windowMin !== "all") {
    if (sortedTrips.length > 0) {
      const nextTrip = sortedTrips.find((t) => (t._refMin ?? 0) >= currentMin) || sortedTrips[0];
      const diff = (nextTrip._refMin ?? 0) - currentMin;
      const displayDiff = diff >= 0 ? `tra ${diff} min` : "passata";
      filterHeaderHtml = `
        <div class="filter-summary-header">
          <span>🕐 Prossima partenza ${displayDiff} · Ora: ${minsToHHMM(currentMin)}</span>
        </div>
      `;
    } else {
      filterHeaderHtml = `
        <div class="filter-summary-header filter-summary-header--empty">
          <span>Nessuna corsa nei prossimi ${windowMin === 60 ? "60" : windowMin} min</span>
        </div>
      `;
    }
  }
  html += filterHeaderHtml;

  // Past-trips toggle (canonical, absorbed from main): one hoisted control
  // above both the Card and Tabella branches. Hidden past trips are filtered
  // inside each branch via state.showPastTrips; the label carries the count.

  const numPastTrips = crossDayBrowsing ? 0 : sortedTrips.filter((t) => (t._refMin ?? 0) < currentMin).length;
  if (numPastTrips > 0) {
    html += `
      <div class="show-past-trips-container" style="margin-bottom: 12px; display: flex; justify-content: flex-start;">
        <button type="button" class="past-toggle show-past-trips-btn" id="toggle-past-trips" style="padding: 6px 12px; font-size: 0.82rem;">
          ${state.showPastTrips ? `↓ Nascondi ${numPastTrips} corse passate` : `↑ Mostra ${numPastTrips} corse passate`}
        </button>
      </div>
    `;
  }

  const thresholds = getThresholdsFromState(state, cfg);

  // P3: detect cross-day browsing — user is browsing a day-type that doesn't
  // match today's actual day-type. When true, suppress countdowns and past/
  // future split (countdown math makes no sense against tomorrow's schedule
  // — every trip would be "partito" or every trip "in 600 min"). Render a
  // neutral flat list with a hint that these are not real-time hours.
  // (Computed early above so the pinned-trip banner can also use it.)

  if (useModernCards) {
    const fromStop = stops[0];
    const toStop = state.timetableToStop || stops[stops.length - 1] || stops[0];

    const futureTrips = [];
    const pastTrips = [];
    if (crossDayBrowsing) {
      // Neutral flat list — no past/future split.
      sortedTrips.forEach((trip) => futureTrips.push(trip));
    } else {
      sortedTrips.forEach((trip) => {
        const isPast = (trip._refMin ?? 0) < currentMin;
        if (isPast) pastTrips.push(trip);
        else futureTrips.push(trip);
      });
    }

    const futureCount = futureTrips.length;
    const pastCount = pastTrips.length;
    const fromName = getStopName(fromStop) || fromStop || "—";
    const toName = getStopName(toStop) || toStop || "—";

    html += `<div class="trips" style="--lc: var(--line-${activeLine}, var(--accent));">`;
    html += renderNextDeparturesStrip(lineData, config, thresholds, currentMin, activeLine, dayType, direction, stops);
    html += `<div class="trips-overview">
      <span class="ln">${escapeHtml(activeLine)}</span>
      <span class="rt">${escapeHtml(fromName)} → ${escapeHtml(toName)}</span>
      <span class="ct">${futureCount + pastCount} corse</span>
    </div>`;
    if (crossDayBrowsing) {
      html += `<div class="banner banner-info cross-day-hint" style="margin:8px 0;">📅 Orari di ${escapeHtml(getDayTypeLabel(dayType))} — non in tempo reale.</div>`;
    }
    html += `<div class="section-head" style="margin-top:0">
      <span class="label">${crossDayBrowsing ? "Tutte le corse" : "Prossime partenze"}</span>
      <span class="sim">stimato da orario</span>
    </div>`;
    // §3: re-expose the time-window quick filter (binding at
    // [data-timetable-filter-window] [data-window] existed but the UI was
    // never rendered — the state could silently apply a window persisted in
    // localStorage. Now the chips are visible; "Tutte" clears the filter.
    if (!crossDayBrowsing) {
      const w = windowMin;
      const winChips = ([
        ["all", "Tutte"],
        ["60", "60 min"],
        ["120", "120 min"]
      ])
        .map(([val, label]) => {
          const isActive =
            (val === "all" && w === "all") ||
            (val !== "all" && w === parseInt(val, 10));
          return `<button type="button" class="win-chip ${isActive ? "active" : ""}" data-window="${val}">${label}</button>`;
        })
        .join("");
      html += `<div class="filter-window" data-timetable-filter-window style="display:flex; gap:6px; margin:4px 0 12px;">${winChips}</div>`;
    }
    const useClusters =
      !state.timetableEditMode && !state.showAllStops && !crossDayBrowsing && windowMin === "all";
    if (useClusters) {
      const clusters = clusterTrips(sortedTrips, currentMin, referenceStops);
      const globalNextTrip = sortedTrips.find((t) => (t._refMin ?? 0) >= currentMin);
      const globalNextTripId = globalNextTrip ? globalNextTrip.tripId : null;

      clusters.forEach((cluster) => {
        // Canonical groups contract: state.showPastTrips forces every cluster
        // open; otherwise only the active (non-collapsed) one is open.
        const isOpen = state.showPastTrips ? true : !cluster.collapsed;
        html += `
          <details class="timetable-cluster"${isOpen ? " open" : ""} id="cluster-${escapeHtml(cluster.id)}">
            <summary class="cluster-summary">
              <span class="cluster-label">${escapeHtml(cluster.label)}</span>
              <span class="cluster-range">${escapeHtml(cluster.range)}</span>
              <span class="cluster-count">${cluster.trips.length} ${cluster.trips.length === 1 ? "corsa" : "corse"}</span>
            </summary>
            <div class="trips-list">`;
        cluster.trips.forEach((trip) => {
          html += renderTripCardFigma(trip, config, thresholds, currentMin, fromStop, toStop, trip.tripId === globalNextTripId, false, activeColor, activeLine, crossDayBrowsing, dayType, direction);
        });
        html += `</div>
          </details>`;
      });
    } else {
      html += `<div class="trips-list">`;
      futureTrips.forEach((trip, idx) => {
        html += renderTripCardFigma(trip, config, thresholds, currentMin, fromStop, toStop, idx === 0 && !crossDayBrowsing, false, activeColor, activeLine, crossDayBrowsing, dayType, direction);
      });
      if (futureTrips.length === 0) {
        html += `<div class="empty-state" style="background: rgba(255,255,255,0.02); border-radius: 16px; padding: 20px;">Nessuna corsa per ${escapeHtml(getDayTypeLabel(dayType))}.</div>`;
      }
      html += `</div>`; // /.trips-list

      // Canonical flat-view contract: past trips are not rendered at all
      // while hidden (the hoisted #toggle-past-trips button re-renders).
      if (pastCount > 0 && state.showPastTrips && !crossDayBrowsing) {
        html += `<div class="trips-list" id="past-list">`;
        pastTrips.slice().reverse().forEach((trip) => {
          html += renderTripCardFigma(trip, config, thresholds, currentMin, fromStop, toStop, false, true, activeColor, activeLine, false, dayType, direction);
        });
        html += `</div>`; // /#past-list
      }
    }
    html += `</div>`; // /.trips
  } else {
    html += renderNextDeparturesStrip(lineData, config, thresholds, currentMin, activeLine, dayType, direction, stops);
    html += `<div class="trips" style="--lc: var(--line-${activeLine}, var(--accent)); padding-bottom: 24px;">
      <div class="section-head" style="margin-top:0">
        <span class="label">Tabellone ${escapeHtml(activeLine)}</span>
        <span class="sim">orario GTFS</span>
      </div>
      <div class="table-card" style="background: var(--surface-fg-3, rgba(255,255,255,0.05)); border: 1px solid var(--surface-fg-6, rgba(255,255,255,0.08)); border-radius: var(--r-xl, 20px); padding: 12px; margin-bottom: 24px;">
        <div class="table-scroll">
          <table class="timetable">
            <thead>
              <tr>
                <th>Corsa</th>
                ${stops
                  .map((code) => {
                    const hasCoords = !!STOP_COORDINATES[code];
                    const label = escapeHtml(shortStop(code));

                    if (state.timetableEditMode) {
                      const isSelected = state.timetableTempStops.includes(code);
                      const badge = isSelected
                        ? `<span class="stop-edit-badge remove" title="Nascondi questa fermata">−</span>`
                        : `<span class="stop-edit-badge add" title="Mostra questa fermata">+</span>`;
                      const colClass = isSelected ? "edit-col" : "edit-col hidden-col";
                      return `<th title="${escapeHtml(getStopName(code))} [${code}]" class="${colClass}" data-edit-stop-code="${code}">${label}${badge}</th>`;
                    }

                    return hasCoords
                      ? `<th title="${escapeHtml(getStopName(code))} [${code}]" class="stop-link" data-stop-code="${code}">${label}</th>`
                      : `<th title="${escapeHtml(getStopName(code))} [${code}]">${label}</th>`;
                  })
                  .join("")}
                <th>Conn.</th>
              </tr>
            </thead>
            <tbody>`;

    const nextIndex = sortedTrips.findIndex((t) => (t._refMin ?? -1) >= currentMin);
    sortedTrips.forEach((trip, index) => {
      // P3: in cross-day browsing mode, all trips are "current-day-neutral" —
      // no past filtering, no ← ora marker, no upcoming +N′ indicator (none of
      // these make sense against a non-today schedule).
      const isPast = crossDayBrowsing ? false : (trip._refMin ?? 0) < currentMin;
      if (isPast && !state.showPastTrips) return;
      const isCurrent = !crossDayBrowsing && index === nextIndex && !isPast;
      const isShort = trip.flags?.includes("short");
      const diff = (trip._refMin ?? 0) - currentMin;
      let connCellHtml = renderConnectionCell(trip, config, thresholds);
      if (isCurrent) {
        connCellHtml += ` <span class="current-indicator">← ora</span>`;
      } else if (!crossDayBrowsing && diff > 0) {
        connCellHtml += ` <span class="upcoming-indicator">+${diff}′</span>`;
      }

      // "Segui questa corsa" — pin button in the classic-table row's first
      // cell. Hidden in edit mode (no point pinning while reordering cols).
      const rowTripId = String(trip.tripId ?? "");
      const pin = window._app_state?.timetablePinnedTrip;
      const isPinnedRow = !!pin &&
        pin.lineId === activeLine &&
        pin.dayType === dayType &&
        pin.direction === direction &&
        String(pin.tripId) === rowTripId;
      const pinCellHtml = state.timetableEditMode
        ? ""
        : `<button type="button" class="trip-pin-btn row-pin${isPinnedRow ? " active" : ""}" data-trip-pin="${escapeHtml(rowTripId)}" data-line="${escapeHtml(activeLine)}" data-day="${escapeHtml(dayType)}" data-dir="${escapeHtml(direction)}" aria-label="${isPinnedRow ? "Rimuovi pin" : "Segui questa corsa"}" title="${isPinnedRow ? "Rimuovi pin" : "Segui questa corsa"}">📌</button>`;

      html += `<tr class="${isPast ? "past" : ""} ${isCurrent ? "current" : ""} ${isShort ? "short" : ""}" data-trip-id="${trip.tripId}" data-key="row-${escapeHtml(String(trip.tripId ?? ""))}">
        <td>
          <strong>#${escapeHtml(trip.tripId || "?")}</strong>
          ${isShort ? `<span class="badge muted">breve</span>` : ""}
          ${pinCellHtml}
        </td>
        ${stops
          .map((code) => {
            const value = trip.stops?.[code];
            const displayValue = value !== undefined && value !== null ? minsToHHMM(value) : "-";

            if (state.timetableEditMode) {
              const isSelected = state.timetableTempStops.includes(code);
              const colClass = isSelected ? "edit-cell" : "edit-cell hidden-col";
              return `<td class="${colClass}">${displayValue}</td>`;
            }
            return `<td>${displayValue}</td>`;
          })
          .join("")}
        <td>${connCellHtml}</td>
      </tr>
      ${!state.timetableEditMode ? renderExpandedRow(trip, config, thresholds, stops.length + 2) : ""}`;
    });

    html += `</tbody></table></div></div>
    </div>`; // close .table-card + .trips wrapper
  }

  const feedValidityStr = cfg.feedValidity
    ? `Orari validi dal ${formatFeedDate(cfg.feedValidity.from)} al ${formatFeedDate(cfg.feedValidity.to)}`
    : "";

  const feedStatus = getFeedStatus(cfg);
  let feedWarningHtml = "";
  if (feedStatus.state === "expired") {
    feedWarningHtml = `<div class="banner banner-danger gtfs-expiry-banner banner-margin-top">⚠️ Orari scaduti — i dati potrebbero non essere aggiornati.</div>`;
  } else if (feedStatus.state === "expiring") {
    feedWarningHtml = `<div class="banner banner-warning gtfs-expiry-banner banner-margin-top">⏳ Orari in scadenza tra ${feedStatus.daysLeft} giorn${feedStatus.daysLeft === 1 ? "o" : "i"}.</div>`;
  }

  html += `
    <div class="app-footer">Fermate visibili personalizzabili in Impostazioni.</div>
    ${feedValidityStr ? `<div class="app-footer feed-validity">${escapeHtml(feedValidityStr)}</div>` : ""}
    <div class="app-footer feed-validity" style="margin-top: 8px;">
      📅 <a href="https://movibus.it/orari-agosto-2026/" target="_blank" rel="noopener noreferrer" style="color: var(--accent); font-weight: 600; text-decoration: underline;">
        Orari Speciali Agosto 2026 su Movibus.it ↗
      </a>
    </div>
    <div class="gtfs-update-actions" style="margin-top: 12px; display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;">
      <button type="button" class="btn text-btn trigger-gtfs-movibus" id="trigger-gtfs-movibus" style="font-size: 0.76rem; padding: 6px 12px; background: rgba(6,182,212,0.1); border: 1px solid rgba(6,182,212,0.25); color: var(--accent); border-radius: 999px;">
        🔄 Aggiorna GTFS Movibus
      </button>
      <button type="button" class="btn text-btn trigger-gtfs-trenord" id="trigger-gtfs-trenord" style="font-size: 0.76rem; padding: 6px 12px; background: rgba(139,92,246,0.1); border: 1px solid rgba(139,92,246,0.25); color: var(--accent-violet, #8b5cf6); border-radius: 999px;">
        🚆 Aggiorna GTFS Trenord
      </button>
    </div>
    ${feedWarningHtml}`;

  const currentSignature = `${activeLine}_${dayType}_${direction}_${state.showAllStops}_${state.timetableClassicView}_${windowMin}`;
  const shouldScroll = state._timetableLastScrollSignature !== currentSignature;
  // V2: when the user switches line/direction/day, stale tripIds from the
  // previous context would otherwise be re-expanded if the same tripId
  // happens to appear in a different itinerary. Reset expansion on signature
  // change so only in-context expansions persist.
  if (shouldScroll && state.timetableExpandedTrips) {
    state.timetableExpandedTrips = {};
  }

  patchDOM(container, html, {
    onAfterPatch: () => {
      bindTimetableEvents(container, { getArgs: () => lastArgs, renderTimetable }, saveSettings);
      if (shouldScroll) {
        state._timetableLastScrollSignature = currentSignature;
        const currentEl = container.querySelector(".timetable tr.current");
        if (currentEl && typeof currentEl.scrollIntoView === "function") {
          currentEl.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      }
    }
  });
}

function renderExpandedConnections(trip, config, thresholds) {
  const parts = [];
  for (const [key, connCfg] of Object.entries(config.connections || {})) {
    const stopCode = connCfg.stopCode || key;
    const arrMin = trip.stops?.[stopCode];
    if (arrMin === undefined || arrMin === null) continue;

    if (connCfg.slotKey) {
      const train = calcNextTrain(connCfg.slotKey, arrMin)[0];
      if (train) {
        const connState = classifyConnection(train.waitMin, thresholds);
        parts.push(`
          <div class="expand-conn-item">
            <span class="conn-pill conn-pill--${connState.css}">${escapeHtml(train.line)}</span>
            <span class="expand-conn-desc">Treno delle <strong>${minsToHHMM(train.departureMin)}</strong> da ${escapeHtml(getStopName(stopCode))} (attesa <strong>${train.waitMin} min</strong> · <span class="conn-state-${connState.css}">${connState.label}</span>)</span>
          </div>
        `);
      }
    } else if (connCfg.type === "M1") {
      parts.push(`
        <div class="expand-conn-item">
          <span class="conn-pill conn-pill--calm">M1</span>
          <span class="expand-conn-desc">Metropolitana M1 da Molino Dorino</span>
        </div>
      `);
    } else {
      parts.push(`
        <div class="expand-conn-item">
          <span class="conn-pill conn-pill--calm">${escapeHtml(connCfg.type)}</span>
          <span class="expand-conn-desc">Coincidenza alle ${minsToHHMM(arrMin)}</span>
        </div>
      `);
    }
  }
  return parts.length ? `<div class="expand-conns">${parts.join("")}</div>` : "";
}

function renderExpandedRow(trip, config, thresholds, numCols) {
  const tripStopsOrdered = Object.entries(trip.stops || {})
    .filter(([_, time]) => time !== undefined && time !== null)
    .sort((a, b) => a[1] - b[1]);

  const verticalStopsHtml = tripStopsOrdered
    .map(([code, time]) => {
      const hasCoords = !!STOP_COORDINATES[code];
      const pinBtn = hasCoords
        ? `<button type="button" class="pin-mini" data-stop-code="${escapeHtml(code)}" data-stop-name="${escapeHtml(getStopName(code))}" aria-label="Apri ${escapeHtml(getStopName(code))} sulla mappa" title="Apri sulla mappa"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></button>`
        : "";
      const isFirstOrLast = code === tripStopsOrdered[0]?.[0] || code === tripStopsOrdered[tripStopsOrdered.length - 1]?.[0];
      return `
      <div class="expand-stop-item${isFirstOrLast ? " is-end" : ""}">
        <span class="expand-stop-time">${minsToHHMM(time)}</span>
        <span class="expand-stop-name">${escapeHtml(getStopName(code))}</span>
        ${pinBtn}
      </div>
    `;
    })
    .join("");

  const expandedConnectionsHtml = renderExpandedConnections(trip, config, thresholds);

  const innerHtml = `
    <div class="expand-content-wrapper">
      <div class="expand-stops-list">
        <h4>Dettaglio fermate</h4>
        ${verticalStopsHtml}
      </div>
      ${
        expandedConnectionsHtml
          ? `
        <div class="expand-connections-list">
          <h4>Coincidenze</h4>
          ${expandedConnectionsHtml}
        </div>
      `
          : ""
      }
    </div>
  `;

  if (numCols === 0) {
    return innerHtml;
  }

  return `
    <tr class="expand-row" id="expand-${trip.tripId}" style="display: none;">
      <td colspan="${numCols}">
        ${innerHtml}
      </td>
    </tr>
  `;
}

// getVisibleStops, getReferenceStops, getReferenceTime moved to timetable-data.js

function renderConnectionCell(trip, config, thresholds) {
  try {
    const parts = [];
    for (const [key, connCfg] of Object.entries(config.connections || {})) {
      // Use explicit stopCode if provided (e.g. BS090_RE maps to stop BS090)
      const stopCode = connCfg.stopCode || key;
      const arrMin = trip.stops?.[stopCode];
      if (arrMin === undefined || arrMin === null) continue;
      if (connCfg.slotKey) {
        const train = calcNextTrain(connCfg.slotKey, arrMin)[0];
        if (train)
          parts.push(
            `<span class="conn-mini">${escapeHtml(train.line)} ${minsToHHMM(train.departureMin)} <small>+${train.waitMin}′</small></span>`
          );
      } else if (connCfg.type !== "M1") {
        parts.push(
          `<span class="conn-mini">${escapeHtml(connCfg.type)} ${minsToHHMM(arrMin)}</span>`
        );
      }
    }
    return parts.length ? parts.join(" ") : "-";
  } catch (e) {
    console.error("[Trasporti] Errore nel calcolo connessioni:", e);
    return "-";
  }
}

// bindEvents moved to timetable-events.js

// getAllStopsOrdered, formatFeedDate moved to timetable-data.js

function renderTripCardFigma(trip, config, thresholds, currentMin, fromStop, toStop, isNext, isPast = false, lineColor, lineId, crossDay = false, dayType, direction) {
  const diff = (trip._refMin ?? 0) - currentMin;
  const depTime = trip.stops?.[fromStop];
  const arrTime = trip.stops?.[toStop];

  const depStr = depTime !== undefined && depTime !== null ? minsToHHMM(depTime) : "-";
  const arrStr = arrTime !== undefined && arrTime !== null ? minsToHHMM(arrTime) : "-";
  const durMin = (depTime != null && arrTime != null && arrTime > depTime) ? (arrTime - depTime) : null;

  const lColor = lineColor || "#06b6d4";
  const lcVar = lineId ? `var(--line-${lineId}, ${lColor})` : lColor;

  // V2: re-apply .expanded after auto-refresh re-renders. State lives on
  // window._app_state (set by store.ts) so the pure renderer doesn't need
  // an extra param threaded all the way through the rendering pipeline.
  const tripId = String(trip.tripId ?? "");
  const isExpanded = !!(window._app_state?.timetableExpandedTrips?.[tripId]);

  // "Segui questa corsa": 📌 button state. Active when this trip matches the
  // persisted pin context (line / day / direction / tripId). The same
  // window._app_state lookup avoids threading the pin through every call.
  const pin = window._app_state?.timetablePinnedTrip;
  const isPinned = !!pin &&
    pin.lineId === lineId &&
    pin.dayType === dayType &&
    pin.direction === direction &&
    String(pin.tripId) === tripId;
  const pinBtnHtml = lineId && dayType && direction
    ? `<button type="button" class="trip-pin-btn${isPinned ? " active" : ""}" data-trip-pin="${escapeHtml(tripId)}" data-line="${escapeHtml(lineId)}" data-day="${escapeHtml(dayType)}" data-dir="${escapeHtml(direction)}" aria-label="${isPinned ? "Rimuovi pin" : "Segui questa corsa"}" title="${isPinned ? "Rimuovi pin" : "Segui questa corsa"}">📌</button>`
    : "";

  // Figma .countdown state: partito / ora / soon (≤5) / mid (≤15) / late (>15)
  // P3: in cross-day browsing mode (user is viewing tomorrow/Saturday's
  // schedule while today is a different day-type), the countdown is nonsense
  // — every trip would read "partito". Suppress it for a neutral display.
  let countdownHtml = "";
  if (crossDay) {
    countdownHtml = "";
  } else if (!isPast) {
    let state = "late";
    let text = `${diff} min`;
    if (diff < 0) { state = "partito"; text = "partito"; }
    else if (diff === 0) { state = "ora"; text = "ora"; }
    else if (diff <= 5) { state = "soon"; text = `${diff} min`; }
    else if (diff <= 15) { state = "mid"; text = `${diff} min`; }
    countdownHtml = `<span class="countdown ${state}">${text}</span>`;
  } else {
    countdownHtml = `<span class="countdown partito">partito</span>`;
  }

  // Transfers (only when present — uses real calcInverseConnections path via config.connections)
  let transfersHtml = "";
  if (!isPast) {
    const pills = [];
    for (const [, connCfg] of Object.entries(config.connections || {})) {
      const stopCode = connCfg.stopCode || "";
      const connArrMin = trip.stops?.[stopCode];
      if (connArrMin === undefined || connArrMin === null) continue;
      if (connCfg.slotKey) {
        const train = calcNextTrain(connCfg.slotKey, connArrMin)[0];
        if (train) {
          const mgColor = train.waitMin < 4 ? "#EF4444" : train.waitMin < 8 ? "#F59E0B" : "#22C55E";
          const icon = train.waitMin < 8 ? "⚡" : "✓";
          const tagBg = (train.line === "M1") ? "rgba(239,68,68,0.2)" : "rgba(34,197,94,0.2)";
          const tagColor = (train.line === "M1") ? "#EF4444" : "#22C55E";
          pills.push(`<span class="transfer-pill">
            <span class="tag" style="background:${tagBg};color:${tagColor};">${escapeHtml(connCfg.type)}</span>
            <span class="dir">a ${escapeHtml(train.line === "M1" ? "Molino Dorino" : train.line)}</span>
            <span class="mg" style="color:${mgColor};">${icon}${train.waitMin}′</span>
          </span>`);
        }
      }
    }
    if (pills.length > 0) {
      transfersHtml = `<div class="transfers">${pills.join("")}</div>`;
    }
  }

  // Trip meta chips
  const metaChips = [];
  metaChips.push(`<span class="chip">Corsa <b>#${escapeHtml(String(trip.tripId ?? "?"))}</b></span>`);
  metaChips.push(`<span class="chip">Validità <b>${escapeHtml(trip.validity || "—")}</b></span>`);
  if (durMin != null) metaChips.push(`<span class="chip">Durata <b>${durMin}′</b></span>`);
  metaChips.push(`<span class="chip"><b>${Object.keys(trip.stops || {}).length}</b> fermate</span>`);

  return `
    <div class="trip-card${isNext ? " is-next" : ""}${isPast ? " is-past" : ""}${isExpanded ? " expanded" : ""}" data-trip-id="${trip.tripId}" data-key="tc-${escapeHtml(tripId)}" style="--lc: ${lcVar};">
      ${isNext ? `
        <div class="trip-next-head">
          <span class="pulse-dot"></span>
          <span class="label">Prossima corsa</span>
        </div>
      ` : ""}
      <div class="trip-main">
        <div class="left"><div>
          <div class="trip-dep">${depStr}</div>
          <div class="trip-arr-row">
            <span class="lbl2">arr</span><span class="arr">${arrStr}</span>
            ${durMin != null ? `<span class="duration-strip">· ${durMin}′</span>` : ""}
          </div>
        </div></div>
        <div class="right-stack">
          ${countdownHtml}
          ${pinBtnHtml}
          <svg class="chev" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>
      </div>
      ${transfersHtml}
      <div class="trip-expand">
        <div class="expand-meta">${metaChips.join("")}</div>
        <div class="expand-stops">${renderExpandedStopsList(trip, fromStop, toStop)}</div>
      </div>
    </div>
  `;
}

// Figma-aligned expand-stops: vertical timeline with .node/.time/.name/.pin-mini
// and a ::before connector line. P10: now slices between fromStop and toStop
// (inclusive) so the expand shows only the segment the user is navigating,
// not the entire trip sequence.
function renderExpandedStopsList(trip, fromStop, toStop) {
  const tripStopsOrdered = Object.entries(trip.stops || {})
    .filter(([, time]) => time !== undefined && time !== null)
    .sort((a, b) => a[1] - b[1]);
  if (!tripStopsOrdered.length) return "";

  // Slice between fromStop and toStop inclusive. If either is undefined, show all.
  let sliced = tripStopsOrdered;
  if (fromStop && toStop) {
    const fromIdx = tripStopsOrdered.findIndex(([code]) => code === fromStop);
    const toIdx = tripStopsOrdered.findIndex(([code]) => code === toStop);
    if (fromIdx !== -1 && toIdx !== -1) {
      const lo = Math.min(fromIdx, toIdx);
      const hi = Math.max(fromIdx, toIdx);
      sliced = tripStopsOrdered.slice(lo, hi + 1);
    }
  }
  if (!sliced.length) return "";
  const lastIdx = sliced.length - 1;
  return sliced
    .map(([code, time], i) => {
      const isEnd = i === 0 || i === lastIdx;
      const hasCoords = !!STOP_COORDINATES[code];
      const pin = hasCoords
        ? `<button type="button" class="pin-mini" data-stop-code="${escapeHtml(code)}" data-stop-name="${escapeHtml(getStopName(code))}" aria-label="Apri ${escapeHtml(getStopName(code))} sulla mappa" title="Apri sulla mappa"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></button>`
        : "";
      return `<div class="expand-stop${isEnd ? " is-end" : ""}">
        <span class="node"></span>
        <span class="time">${minsToHHMM(time)}</span>
        <span class="name">${escapeHtml(getStopName(code))}</span>
        ${pin}
      </div>`;
    })
    .join("");
}
// "Prossime corse" strip: the next departures from the selected Da stop for
// the active line/day/direction. Tapping an entry expands + scrolls to that
// trip card (expansion state lives in state.timetableExpandedTrips, V2).
function renderNextDeparturesStrip(lineData, config, thresholds, currentMin, activeLine, dayType, direction, stops) {
  const state = window._app_state || {};
  const fromStop = stops[0];
  const toStop = state.timetableToStop || stops[stops.length - 1] || stops[0];
  const next = getNextDepartures(lineData, {
    lineId: activeLine,
    stopId: fromStop,
    dayType,
    direction: state.timetableDirection || "outbound",
    fromMin: currentMin,
    limit: 3
  });
  if (!next.length) return "";

  const key = getScheduleKey(activeLine, dayType, direction);
  const allTrips = lineData?.[activeLine]?.[key] || [];
  const entries = next
    .map((e) => {
      const trip = allTrips.find((t) => String(t.tripId ?? "") === e.tripId);
      if (!trip) return null;
      const depTime = trip.stops?.[fromStop];
      const arrTime = trip.stops?.[toStop];
      return {
        tripId: e.tripId,
        depStr: minsToHHMM(depTime),
        arrStr: arrTime !== undefined && arrTime !== null ? minsToHHMM(arrTime) : "—",
        wait: depTime - currentMin
      };
    })
    .filter(Boolean);
  if (!entries.length) return "";

  const items = entries
    .map((en) => {
      const cdClass = en.wait === 0 ? "ora" : en.wait <= 5 ? "soon" : en.wait <= 15 ? "mid" : "late";
      return `<button type="button" class="trip-card is-next is-next-dep" data-next-dep="${escapeHtml(en.tripId)}" style="--lc: var(--line-${activeLine}, #06b6d4);">
        <span class="nd-time">${en.depStr}</span>
        <span class="nd-arrow">→</span>
        <span class="nd-arr">${en.arrStr}</span>
        <span class="countdown ${cdClass}">${en.wait === 0 ? "ora" : `${en.wait} min`}</span>
      </button>`;
    })
    .join("");

  return `
    <div class="next-departures" data-next-departures>
      <div class="section-head">
        <span class="label">Prossime corse da ${escapeHtml(getStopName(fromStop))}</span>
      </div>
      ${items}
    </div>
  `;
}
